import React, { FunctionComponent, useRef, useState } from 'react'
import { Typography, Button, Input, Space, Table, InputRef } from 'antd'
import { useLoaderData, useMatch, useRouter } from '@tanstack/react-router'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import type { ColumnType } from 'antd/es/table'
import type { FilterConfirmProps } from 'antd/es/table/interface'
import { formatPhoneNumber } from '../../../../utils/phone'
import { Vehicle, Agencies } from 'server'
import { getActiveVehicles } from '../../../../utils/units'

type DataIndex = keyof Agencies

const AgenciesTable: FunctionComponent<any> = () => {
  const match = useMatch({ strict: false })
  const { vehicles, vehicleLocations, agencies } = useLoaderData({ from: match.routeId ?? '/' })

  const router = useRouter()

  const [searchText, setSearchText] = useState('')
  const [searchedColumn, setSearchedColumn] = useState('')
  const searchInput = useRef<InputRef>(null)

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex
  ) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex.toString())
  }

  const handleReset = (clearFilters: () => void) => {
    clearFilters()
    setSearchText('')
  }

  const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Vehicle> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex.toString()}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false })
              setSearchText((selectedKeys as string[])[0])
              setSearchedColumn(dataIndex.toString())
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
    onFilter: (value, record) => {
      if (!record[dataIndex]) return false
      return record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase())
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      )
  })

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      ...getColumnSearchProps('id'),
      sorter: (a: Agencies, b: Agencies) => a.id - b.id,
      render: (text: string, record: any, index: number) => {
        return <a target={'_blank'} rel={'noreferrer'} href={'/admin/agencies/' + record.id}>{text}</a>
      }
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
      sorter: (a: Agencies, b: Agencies) => a.name.localeCompare(b.name)
    },
    {
      title: 'Friendly Name',
      dataIndex: 'friendly_name',
      key: 'friendly_name',
      ...getColumnSearchProps('friendly_name'),
      sorter: (a: Agencies, b: Agencies) => a.friendly_name.localeCompare(b.friendly_name)
    },
    {
      title: 'Active Vehicles',
      key: 'vehicles',
      defaultSortOrder: 'descend' as const,
      sorter: (a: Agencies, b: Agencies) => {
        const aVehicles = getActiveVehicles(getVehicleCount(getVehicleIds(a.id)))
        const bVehicles = getActiveVehicles(getVehicleCount(getVehicleIds(b.id)))
        return aVehicles - bVehicles
      },
      render: (text: string, record: any, index: number) => {
        const vehicleIds = getVehicleIds(record.id)
        const vehicleCount = getVehicleCount(vehicleIds)
        const activeVehicles = getActiveVehicles(vehicleCount)
        return <span>{`${activeVehicles} / ${vehicleIds.length}`}</span>
      },
    },
    {
      title: 'Dispatch Phone Number',
      dataIndex: 'dispatch_phone_number',
      key: 'dispatch_phone_number',
      ...getColumnSearchProps('dispatch_phone_number'),
      render: (text: string, record: any, index: number) => {
        return text ? <Typography.Text copyable={true}>{formatPhoneNumber(text)}</Typography.Text> : '---'
      },
      sorter: (a: Agencies, b: Agencies) => {
        if (!a.dispatch_phone_number) return -1
        if (!b.dispatch_phone_number) return 1
        return a.dispatch_phone_number.localeCompare(b.dispatch_phone_number)
      }
    }
  ]

  const getVehicleIds = (id: number) => vehicles.filter((_: Vehicle) => _.agency_id === id).map((_: Vehicle) => _.vehicle_id)
  const getVehicleCount = (vehicleIds: number[]) => vehicleLocations.filter((_: Vehicle) => vehicleIds.includes(_.vehicle_id))

  return (
    <Table
      style={{
        cursor: 'pointer',
        margin: '12px'
      }}
      pagination={{ pageSize: 100 }}
      onRow={(record, rowIndex) => {
        return {
          onClick: (event) => {
            const url = router.buildLocation({ to: `/admin/agencies/${record.id}` }).href;
            window.open(url, '_blank'); // Opens in a new tab
          }
        }
      }}
      bordered={true}
      size={'small'}
      dataSource={agencies}
      columns={columns}
      rowKey={'id'}
    />
  )
}

export default AgenciesTable
